import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getInitials } from '../../utils/bookingFormat';
import type { PublicBookingStaff } from '../../types/publicBooking';

interface StylistWorkModalProps {
	stylist: PublicBookingStaff;
	/** Portrait fallback colour, matched to the stylist's card in the grid. */
	gradient: string;
	onClose: () => void;
	onBook: () => void;
}

/**
 * The portfolio a customer judges a stylist by — so the photos, not the chrome,
 * get the room.
 *
 * The mockup paged through one photo at a time on a cream card, which hides how
 * much work there is and makes browsing a guessing game. Three changes fix that:
 *
 *  1. The stage is dark. Hair photos on a cream surface fight the card for
 *     attention; on near-black they read as the brightest thing on screen.
 *  2. A thumbnail filmstrip sits under the stage. The whole portfolio is
 *     visible at a glance and any photo is one click away, instead of five
 *     presses of an arrow.
 *  3. Neighbours are preloaded, so stepping through does not flash empty.
 *
 * Arrows, swipe and ←/→ still work for anyone who prefers to page.
 */
const StylistWorkModal = ({ stylist, gradient, onClose, onBook }: StylistWorkModalProps) => {
	// Memoised: the preload effect below depends on it, and a fresh array every
	// render would re-run that on every keystroke and click.
	const photos = useMemo(
		() => stylist.portfolioImageUrls?.filter(Boolean) ?? [],
		[stylist.portfolioImageUrls]
	);
	const [index, setIndex] = useState(0);
	const [failed, setFailed] = useState<Record<number, boolean>>({});
	const closeRef = useRef<HTMLButtonElement>(null);
	const stripRef = useRef<HTMLDivElement>(null);
	const touchStartX = useRef<number | null>(null);

	const hasMany = photos.length > 1;

	const step = useCallback(
		(direction: number) => {
			if (photos.length < 2) return;
			setIndex(current => (current + direction + photos.length) % photos.length);
		},
		[photos.length]
	);

	// Escape and arrow keys are bound to the document rather than the panel so
	// they work no matter where focus has landed inside the dialog.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
			if (event.key === 'ArrowLeft') step(-1);
			if (event.key === 'ArrowRight') step(1);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [onClose, step]);

	// Hold the page still behind the overlay, and give focus somewhere sensible
	// inside it so keyboard and screen-reader users are not left on the page.
	useEffect(() => {
		const previous = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		closeRef.current?.focus();
		return () => {
			document.body.style.overflow = previous;
		};
	}, []);

	// Warm the neighbours so paging does not show an empty frame on a slow link.
	useEffect(() => {
		[index - 1, index + 1].forEach(neighbour => {
			const url = photos[(neighbour + photos.length) % photos.length];
			if (url) new Image().src = url;
		});
	}, [index, photos]);

	// Keep the active thumbnail in view when paging with arrows or keys.
	useEffect(() => {
		const strip = stripRef.current;
		const active = strip?.children[index] as HTMLElement | undefined;
		active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
	}, [index]);

	const topServices = (stylist.topServices ?? []).slice(0, 3);
	const busiest = topServices.reduce((max, service) => Math.max(max, service.count), 0) || 1;
	const current = photos[index];

	return (
		<div
			className="pb-work"
			role="presentation"
			onClick={event => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div
				className="pb-work__panel"
				role="dialog"
				aria-modal="true"
				aria-label={`${stylist.name}'s work`}
			>
				<div className="pb-work__header">
					<div className="pb-work__identity">
						<span className="pb-work__avatar" style={{ background: gradient }}>
							{photos[0] && !failed[0] ? (
								<img src={photos[0]} alt="" />
							) : (
								getInitials(stylist.name)
							)}
						</span>
						<span>
							<span className="pb-work__name">{stylist.name}</span>
							{(stylist.grade || stylist.role) && (
								<span className="pb-work__role">
									{stylist.grade
										? stylist.grade.charAt(0) +
											stylist.grade.slice(1).toLowerCase()
										: stylist.role}
								</span>
							)}
						</span>
					</div>
					<button
						ref={closeRef}
						type="button"
						className="pb-work__close"
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>

				<div className="pb-work__body">
					{/* Dark stage — the photo is the brightest thing on screen. */}
					<div
						className="pb-work__stage"
						onTouchStart={event => {
							touchStartX.current = event.touches[0].clientX;
						}}
						onTouchEnd={event => {
							if (touchStartX.current === null) return;
							const dx = event.changedTouches[0].clientX - touchStartX.current;
							touchStartX.current = null;
							if (dx > 45) step(-1);
							else if (dx < -45) step(1);
						}}
					>
						{current && !failed[index] ? (
							<img
								key={current}
								className="pb-work__photo"
								src={current}
								alt={`${stylist.name}'s work, photo ${index + 1} of ${photos.length}`}
								onError={() => setFailed(prev => ({ ...prev, [index]: true }))}
							/>
						) : (
							<div className="pb-work__empty">
								<span className="pb-work__empty-icon">✂️</span>
								<span>
									{photos.length === 0
										? 'No photos yet'
										: 'This photo could not be loaded'}
								</span>
							</div>
						)}

						{hasMany && (
							<>
								<button
									type="button"
									className="pb-work__nav pb-work__nav--prev"
									onClick={() => step(-1)}
									aria-label="Previous photo"
								>
									&#8249;
								</button>
								<button
									type="button"
									className="pb-work__nav pb-work__nav--next"
									onClick={() => step(1)}
									aria-label="Next photo"
								>
									&#8250;
								</button>
								<div className="pb-work__counter">
									{index + 1} / {photos.length}
								</div>
							</>
						)}
					</div>

					{/* Filmstrip — the whole portfolio at a glance, one click to any of it. */}
					{hasMany && (
						<div className="pb-work__strip" ref={stripRef}>
							{photos.map((url, position) => (
								<button
									key={url}
									type="button"
									className={`pb-work__thumb${position === index ? ' is-active' : ''}`}
									onClick={() => setIndex(position)}
									aria-label={`Photo ${position + 1}`}
									aria-current={position === index}
								>
									<img src={url} alt="" loading="lazy" />
								</button>
							))}
						</div>
					)}

					<div className="pb-work__stats">
						<div className="pb-work__stats-head">
							<span className="pb-work__stats-num">{stylist.completedCount ?? 0}</span>
							<span className="pb-work__stats-num-label">appointments completed</span>
						</div>
						<div className="pb-work__stats-title">Top services performed</div>
						{topServices.length > 0 ? (
							topServices.map(service => (
								<div key={service.name} className="pb-work__bar-row">
									<div className="pb-work__bar-top">
										<span>{service.name}</span>
										<span className="pb-work__bar-count">{service.count}</span>
									</div>
									<div className="pb-work__bar">
										<div
											className="pb-work__bar-fill"
											style={{
												width: `${Math.round((service.count / busiest) * 100)}%`,
											}}
										/>
									</div>
								</div>
							))
						) : (
							<div className="pb-work__stats-empty">No services logged yet.</div>
						)}
					</div>
				</div>

				<div className="pb-work__footer">
					<button type="button" className="pb__btn pb__btn--primary" onClick={onBook}>
						Book with This Stylist
					</button>
				</div>
			</div>
		</div>
	);
};

export default StylistWorkModal;
